import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon';
import Button from '../Button';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Chevron from '../../svg/icons/ic_chevron-left_20x20.svg';
import { getPageContext } from '../../utils';

import './DropdownGroup.scss';

const testId = 'dropdown-group';
export const PARTY_SIZE_ID = 'party_size';
export const DATE_SELECTOR_ID = 'date';
export const TIME_SELECTOR_ID = 'time';

let futureDateTimeOptions;

const renderTimeOptions = (selectedDateFormatted) => {
  let startTime = moment('06:00', 'HH:mm');

  if (selectedDateFormatted === 'Today') {
    const now = moment();
    const remainder = 30 - (now.minute() % 30);
    startTime = now.add(remainder, 'minutes').format('HH:mm');
  } else if (futureDateTimeOptions) {
    // use memoized value if already calculated, and if selectedDateFormatted is NOT today
    return futureDateTimeOptions;
  }
  const allDayLabel = getTranslation({ translationKey: 'all_day', fallback: 'All Day' });
  const range = [<option label={allDayLabel} value={null} key="all-day">{allDayLabel}</option>];

  let timeDisplay;
  let timeValue;
  const endTime = moment('02:00', 'HH:mm').add(1, 'day');

  const tempTime = moment(startTime, 'HH:mm');
  while (tempTime <= endTime) {
    timeDisplay = tempTime.format('LT');
    timeValue = tempTime.format('HHmm');

    range.push(<option label={timeDisplay} value={timeValue} key={timeValue}>{timeDisplay}</option>);
    tempTime.add(30, 'minutes');
  }

  if (selectedDateFormatted !== 'Today') {
    futureDateTimeOptions = range;
  }

  return range;
};

const DropdownGroup = ({
  Analytics,
  calendarOpen,
  isMobile,
  isLargePartyConfigured,
  maxPartySize,
  minPartySize,
  onPartySizeChange,
  onPartySizeClick,
  onTimeChange,
  onTimeClick,
  selectedDateFormatted,
  selectedQuantity,
  searchSessionId,
  toggleCalendar,
  dateLabel,
  selectedTime,
  shopSmallValue,
  centurionNyValue,
  timeOptions,
}) => {
  const [isPartySizeOpen, setIsPartySizeOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const renderPartySizeOptions = () => {
    let maxPartySizeUpdated = maxPartySize;
    if (maxPartySize > 6 && shopSmallValue) {
      maxPartySizeUpdated = 6;
    }

    const options = minPartySize > 1 ? [] : [
      {
        value: '1', label: '1 Guest', key: 1, translationKey: 'one_guest',
      },
    ];

    const min = Math.max(minPartySize, 2);
    const max = maxPartySizeUpdated || 20;

    for (let i = min; i <= max; i += 1) {
      options.push({
        value: `${i}`,
        label: `${i} Guests`,
        key: `${i} guests`,
        translationKey: 'number_guests',
        translationValues: { number: `${i}` },
      });
    }

    if (isLargePartyConfigured && !shopSmallValue) {
      const largePartySize = max + 1;
      options.push({
        value: `${largePartySize}`,
        label: `${largePartySize}+ Guests`,
        key: `${largePartySize}`,
        translationKey: 'number_guests',
        translationValues: { number: `${largePartySize}` },
      });
    }

    return options.map((option, idx) => {
      let label;
      if (isLargePartyConfigured && options.length === idx + 1) {
        label = option.label;
      } else {
        label = getTranslation({
          translationKey: option.translationKey,
          translationValues: option.translationValues,
          fallback: option.label,
        });
      }
      return (
        <option
          key={option.key}
          value={option.value}
          label={label}
        >
          {label}
        </option>
      );
    });
  };

  const handleTimeChange = (event) => {
    event.preventDefault();
    onTimeChange({ time: event.target.value });
  };

  const handleDateSelectorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCalendar();
  };

  const handlePartySizeChange = (event) => {
    event.preventDefault();
    Analytics.event('complete search', {
      context: getPageContext(window.location.pathname),
      'filter type': 'search filter',
      value: event.target.value,
      searchSessionId,
    });

    onPartySizeChange({ party_size: event.target.value, previous: selectedQuantity });
  };

  const handlePartySizeFocus = (event) => {
    event.preventDefault();
    onPartySizeClick();
    setIsPartySizeOpen(true);
  };

  const handlePartySizeBlur = (event) => {
    event.preventDefault();
    setIsPartySizeOpen(false);
  };

  const handleTimePickerFocus = (event) => {
    event.preventDefault();
    onTimeClick();
    setIsTimePickerOpen(true);
  };

  const handleTimePickerBlur = (event) => {
    event.preventDefault();
    setIsTimePickerOpen(false);
  };

  return (
    <div className={cx('DropdownGroup', { 'DropdownGroup--mobile': isMobile })}>
      <div className="DropdownGroup__selector DropdownGroup__selector--party">
        <label htmlFor={PARTY_SIZE_ID}>
          {!isMobile && <TranslationProvider translationKey="guests">Guests</TranslationProvider>}
          <select
            id={PARTY_SIZE_ID}
            className="DropdownGroup__party-size-select"
            data-test-id={`${testId}-party-size`}
            name={PARTY_SIZE_ID}
            onChange={handlePartySizeChange}
            onFocus={handlePartySizeFocus}
            onBlur={handlePartySizeBlur}
            value={`${selectedQuantity}`}
          >
            {renderPartySizeOptions()}
          </select>
          {!isMobile && <Icon component={Chevron} name="Chevron" />}
        </label>
      </div>

      <Button
        id="DropdownGroup__selector--date"
        data-test-id={`${testId}-date-selector`}
        className={cx('DropdownGroup__selector', 'DropdownGroup__selector--date', {
          'DropdownGroup__selector--date--active': calendarOpen,
        })}
        variant="no-style"
        onClick={handleDateSelectorClick}
        tabIndex={0}
        {...((shopSmallValue || centurionNyValue) ? { disabled: true } : {})}
      >
        <label htmlFor={DATE_SELECTOR_ID}>
          {!isMobile && <TranslationProvider translationKey="date">Date</TranslationProvider>}
          <div
            id="DropdownGroup__selector--date--selection"
            tabIndex={-1}
            className={cx('DropdownGroup__selector--date--selection', {
              disabled: (shopSmallValue || centurionNyValue),
            })}
          >
            {dateLabel || ''}
          </div>
          {!isMobile && <Icon component={Chevron} name="Chevron" className={cx({ 'ResyIcon--Chevron--open': calendarOpen })} />}
        </label>
      </Button>

      <div
        className={cx('DropdownGroup__selector DropdownGroup__selector--time', {
          disabled: shopSmallValue,
        })}
      >
        <label htmlFor={TIME_SELECTOR_ID}>
          {!isMobile && <TranslationProvider translationKey="invite_time_label">Time</TranslationProvider>}
          <select
            id={TIME_SELECTOR_ID}
            name={TIME_SELECTOR_ID}
            onChange={handleTimeChange}
            onFocus={handleTimePickerFocus}
            onBlur={handleTimePickerBlur}
            value={selectedTime || ''}
            {...(shopSmallValue ? { disabled: true } : {})}
            className={cx('DropdownGroup__time-select', {
              disabled: shopSmallValue,
            })}
          >
            {timeOptions || renderTimeOptions(selectedDateFormatted)}
          </select>
          {!isMobile && <Icon component={Chevron} name="Chevron" />}
        </label>
      </div>

      <div
        className={cx('DropdownGroup__divider DropdownGroup__divider--party', {
          'DropdownGroup__divider--hidden': isPartySizeOpen,
        })}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div
        className={cx('DropdownGroup__divider DropdownGroup__divider--time', {
          'DropdownGroup__divider--hidden': isTimePickerOpen,
        })}
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
};

DropdownGroup.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  calendarOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLargePartyConfigured: PropTypes.bool,
  maxPartySize: PropTypes.number,
  minPartySize: PropTypes.number,
  onPartySizeChange: PropTypes.func,
  onPartySizeClick: PropTypes.func,
  onTimeChange: PropTypes.func,
  onTimeClick: PropTypes.func,
  selectedDateFormatted: PropTypes.string.isRequired,
  selectedQuantity: PropTypes.number,
  toggleCalendar: PropTypes.func.isRequired,
  dateLabel: PropTypes.string.isRequired,
  selectedTime: PropTypes.string,
  searchSessionId: PropTypes.string,
  shopSmallValue: PropTypes.bool,
  centurionNyValue: PropTypes.bool,
  timeOptions: PropTypes.arrayOf(),
};

DropdownGroup.defaultProps = {
  calendarOpen: false,
  isMobile: false,
  isLargePartyConfigured: false,
  maxPartySize: undefined,
  minPartySize: undefined,
  onPartySizeChange: () => {},
  onPartySizeClick: () => {},
  onTimeChange: () => {},
  onTimeClick: () => {},
  selectedQuantity: 2,
  selectedTime: '',
  shopSmallValue: false,
  centurionNyValue: false,
  timeOptions: null,
  searchSessionId: '',
};

const mapStateToProps = ({ venueSearch }) => ({
  searchSessionId: venueSearch?.metadata?.searchSessionId,
});

export default connect(mapStateToProps)(DropdownGroup);
